import React, { useState, useEffect } from 'react';
import { Group, Button, Drawer, Stack, Image, ActionIcon, Avatar, Menu, Text, UnstyledButton, useMantineTheme } from '@mantine/core';
import { useNavigate, useLocation } from 'react-router-dom';
import './NavBar.css';
import { IconSettings, IconLogout, IconCalendar, IconUsers, IconCreditCard, IconMenu2, IconChevronDown, IconChevronRight, IconSpeakerphone } from '@tabler/icons-react';
import { useSalon } from '../../context/SalonContext';
import { useMediaQuery } from '@mantine/hooks';
import { signOut } from 'aws-amplify/auth';
import { useAuth } from '../../context/AuthContext';

function NavBar({ hideNavLinks = false, onboardingStage = null }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const { currentUser, businessInfo } = useSalon();
  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
  const { resetAuth } = useAuth();

  const navItems = [
    { 
      label: 'Calendar', 
      path: '/admin/calendar',
      icon: IconCalendar
    },
    { 
      label: 'Clients', 
      path: '/admin/clients',
      icon: IconUsers
    },
    // Only show Marketing for owners/managers
    ...(currentUser?.role === 'Owner' || currentUser?.role === 'Manager' ? [{
      label: 'Marketing',
      path: '/admin/marketing',
      icon: IconSpeakerphone
    }] : []),
  ];

  const paymentsSubLinks = [
    { label: 'Overview', path: '/admin/payments/overview' },
    { label: 'Devices', path: '/admin/payments/devices' },
    { label: 'Stripe Settings', path: '/admin/payments/settings' },
  ];

  const [paymentsExpanded, setPaymentsExpanded] = useState(false);

  // Auto-expand Payments if on a payments route
  useEffect(() => {
    if (location.pathname.startsWith('/admin/payments/')) {
      setPaymentsExpanded(true);
    }
  }, [location.pathname]);

  const isPaymentsActive = paymentsSubLinks.some(link => location.pathname.startsWith(link.path));

  const NavButton = ({ icon: Icon, label, path, onClick }) => (
    <UnstyledButton
      onClick={onClick ? onClick : () => {
        setIsExpanded(false);
        navigate(path);
      }}
      p="xs"
      className={`nav-item ${location.pathname === path ? 'active' : ''}`}
    >
      <Group 
        wrap="nowrap" 
        style={{ 
          minHeight: '24px',
          overflow: 'hidden'
        }}
      >
        {Icon && (
          <Icon 
            size={20} 
            style={{ 
              flexShrink: 0,
              minWidth: '20px'
            }} 
          />
        )}
        <Text 
          size="sm"
          className={`nav-item-text ${location.pathname === path ? 'active' : ''}`}
          style={{
            opacity: isExpanded ? 1 : 0,
            whiteSpace: 'nowrap'
          }}
        >
          {label}
        </Text>
      </Group>
    </UnstyledButton>
  );

  const handleSignOut = async () => {
    try {
      await signOut();
      resetAuth();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const DesktopNav = () => (
    <div 
      className={`navbar-sidebar${isExpanded ? ' expanded' : ''}`}
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
      style={{
        transition: 'width 200ms ease'
      }}
    >
      <Stack justify="space-between" h="100%" p="xs">
        <Stack gap="xs">
          {!hideNavLinks && (
            <>
              {navItems.map((item) => (
                !item.hidden && (
                  <NavButton key={item.path} {...item} />
                )
              ))}
              {/* Payments parent section */}
              {businessInfo?.stripe_connect_account_is_onboarded && currentUser?.id === businessInfo?.primary_owner_id && (
                <>
                  <UnstyledButton
                    onClick={() => {
                      setPaymentsExpanded((prev) => !prev);
                    }}
                    p="xs"
                    className={`nav-item ${isPaymentsActive ? 'active' : ''}`}
                  >
                    <Group wrap="nowrap" justify="space-between">
                      <Group wrap="nowrap">
                        <IconCreditCard size={20} />
                        <Text 
                          size="sm"
                          className="nav-item-text"
                          style={{
                            opacity: isExpanded ? 1 : 0,
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Payments
                        </Text>
                      </Group>
                      {isExpanded && (paymentsExpanded ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />)}
                    </Group>
                  </UnstyledButton>
                  {/* Payments sub-links */}
                  {isExpanded && paymentsExpanded && (
                    <Stack gap={6}>
                      {paymentsSubLinks.map((sub) => (
                        <UnstyledButton
                          key={sub.path}
                          onClick={() => {
                            setPaymentsExpanded(false);
                            navigate(sub.path);
                          }}
                          p="xs"
                          pl={50}
                          justify="center"
                          className={`nav-item ${location.pathname.startsWith(sub.path) ? 'active' : ''}`}
                        >
                          <Text size="sm" className="nav-item-text">{sub.label}</Text>
                        </UnstyledButton>
                      ))}
                    </Stack>
                  )}
                </>
              )}
            </>
          )}
        </Stack>

        <Stack gap="xs">
          <Group w="200px" px="xs">
            <Text size="sm" c="white" style={{ opacity: isExpanded ? 1 : 0, transition: 'opacity 200ms ease' }}>
              {`Signed in as ${currentUser?.first_name} ${currentUser?.last_name}`}
            </Text>
          </Group>
          {onboardingStage && (
            <Button
              onClick={() => navigate(`/${onboardingStage}`)}
              sx={{
                borderRadius: '100px',
                padding: '10px 20px'
              }}
            >
              Continue Onboarding
            </Button>
          )}
          <NavButton
            icon={IconSettings}
            label="Settings"
            path="/admin/settings/studio"
          />
          <NavButton
            icon={IconLogout}
            label="Sign Out"
            onClick={handleSignOut}
          />

        </Stack>
      </Stack>
    </div>
  );

  const MobileNav = () => (
    <>
      <div className="navbar mobile-nav">
        <Group style={{ width: '100%' }} justify="space-between">
          <ActionIcon
            onClick={() => setMobileMenuOpen(true)}
            className="menu-icon"
          >
            <IconMenu2 size={30} />
          </ActionIcon>
          <div className="salon-title">{businessInfo?.salon_name}</div>
          <ActionIcon size="lg" radius="xl" variant="subtle" className="user-avatar-button" disabled>
            <Avatar radius="xl" color="violet">
              {currentUser?.first_name?.[0] || 'U'}
            </Avatar>
          </ActionIcon>
        </Group>
      </div>

      <Drawer
        opened={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        size="100%"
        position="left"
        title={<Text size="sm">{businessInfo?.salon_name} (Signed in as {currentUser?.first_name} {currentUser?.last_name})</Text>}
        zIndex={1000}
      >
        <Stack spacing="lg" p="md" zIndex={1000}>
          {navItems.map(item => (
            !item.hidden && (
              <Button
                key={item.path}
                variant="subtle"
                color="violet"
                fullWidth
                className={location.pathname === item.path ? 'active' : ''}
                leftSection={<item.icon size={16} />}
                onClick={() => {
                  navigate(item.path);
                  setMobileMenuOpen(false);
                }}
              >
                {item.label}
              </Button>
            )
          ))}
          {/* Payments expandable section in mobile */}
          {businessInfo?.stripe_connect_account_is_onboarded && currentUser?.id === businessInfo?.primary_owner_id && (
            <>
              <Button
                variant="subtle"
                color="violet"
                fullWidth
                rightSection={paymentsExpanded ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
                leftSection={<IconCreditCard size={16} />}
                onClick={() => setPaymentsExpanded((prev) => !prev)}
                className={isPaymentsActive ? 'active' : ''}
              >
                Payments
              </Button>
              {paymentsExpanded && (
                <Stack gap={0} pl={24}>
                  {paymentsSubLinks.map((sub) => (
                    <Button
                      key={sub.path}
                      variant="subtle"
                      color="violet"
                      fullWidth
                      className={location.pathname.startsWith(sub.path) ? 'active' : ''}
                      onClick={() => {
                        navigate(sub.path);
                        setMobileMenuOpen(false);
                      }}
                      style={{ textAlign: 'left', justifyContent: 'flex-start' }}
                    >
                      {sub.label}
                    </Button>
                  ))}
                </Stack>
              )}
            </>
          )}
          <Button
            variant="subtle"
            color="violet"
            fullWidth
            className={location.pathname.startsWith('/admin/settings') ? 'active' : ''}
            leftSection={<IconSettings size={16} />}
            onClick={() => {
              navigate('/admin/settings/studio');
              setMobileMenuOpen(false);
            }}
          >
            Settings
          </Button>
          <Button
            variant="light"
            color="red"
            fullWidth
            leftSection={<IconLogout size={16} />}
            onClick={handleSignOut}
          >
            Sign Out
          </Button>
        </Stack>
      </Drawer>
    </>
  );

  return isMobile ? <MobileNav /> : <DesktopNav />;
}

export default NavBar;